/**
 * The wild pup's romp on the grass (Pets v2, docs/PETS.md), the parts that need no GL: where it may run (open grass by
 * the road, inside the map), what it does next (a dash to another spot, the zoomies, a play-bow, a roll, a sniff), and
 * where it runs off to when it leaves. Pure (randomness injected), so test/wildPet.test.ts pins it. Presentation only:
 * the sim never places it — only its window (WildPetAppeared → Left / Engaged) is the sim's.
 */

export interface Spot { readonly x: number; readonly z: number }
/** One thing the wild pup does: a dash to a spot, or a clip in place for `seconds`. */
export type Romp =
  | { readonly kind: "dash"; readonly to: Spot }
  | { readonly kind: "zoomies" | "playbow" | "roll" | "sniff"; readonly seconds: number };

export const WILD_ROMP = {
  /** Grass this many cells (Chebyshev) from a road cell, at most, and never on the map's outer ring. */
  nearRoad: 2,
  /** The middle share of the road it romps by (the portal's end and the gate's are crowded or off the shot). */
  roadFrom: 0.25, roadTo: 0.8,
  /** Odds of a dash next (never two running), and the weights of the clips in place otherwise. */
  dash: 0.45, zoomies: 0.4, playbow: 0.25, roll: 0.2, sniff: 0.15,
  /** Seconds of each clip in place: the zoomies loop for a while; the one-shots their own length (the clips'). */
  zoomiesSeconds: [1.8, 3] as const, playbowSeconds: 1.0, rollSeconds: 1.4, sniffSeconds: 2.0,
  /** A dash goes this far at most (cells), to another spot near where it is. */
  dashReach: 3,
  /** The spot jitters this much inside its cell (cells). */
  jitter: 0.35,
  /** When it leaves untapped it scampers this far at most (cells) before it vanishes in a puff. */
  leaveReach: 4,
} as const;

/**
 * Open grass cells by the middle of the road: within `nearRoad` of one of its road cells (from `roadFrom` to `roadTo`
 * of the way from the portal), `open` (grass, no tower, rock or hole), never on the outer ring. `road` runs portal → gate.
 */
export function rompCells(road: readonly { readonly x: number; readonly y: number }[], width: number, height: number, open: (x: number, y: number) => boolean): Spot[] {
  const out: Spot[] = [], seen = new Set<number>();
  const a = Math.floor(road.length * WILD_ROMP.roadFrom), b = Math.max(a + 1, Math.ceil(road.length * WILD_ROMP.roadTo));
  const r = WILD_ROMP.nearRoad;
  for (const c of road.slice(a, b)) {
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      const x = c.x + dx, y = c.y + dy, k = y * width + x;
      if (x <= 0 || y <= 0 || x >= width - 1 || y >= height - 1 || seen.has(k)) continue;
      seen.add(k);
      if (open(x, y)) out.push({ x, z: y });
    }
  }
  return out;
}

/** A spot in cell `c`, jittered by two draws in [0, 1). */
export function jittered(c: Spot, r1: number, r2: number): Spot {
  return { x: c.x + (r1 - 0.5) * 2 * WILD_ROMP.jitter, z: c.z + (r2 - 0.5) * 2 * WILD_ROMP.jitter };
}

/** Steps (cells) between the samples of a straight run checked by `clearRun`. */
const RUN_STEP = 0.2;

/**
 * Whether a straight run from `a` to `b` stays on open ground the whole way: every sample's cell is `open` (grass —
 * never a canyon, a cliff, rock, a tower or the road's far side over a hole). A dash or a getaway that would cross one is
 * never taken, so the pup never runs out over a canyon (Kevin, 2026-10-14: "it looks like the pup falls into the canyon").
 */
export function clearRun(a: Spot, b: Spot, open: (x: number, y: number) => boolean): boolean {
  const n = Math.max(1, Math.ceil(Math.hypot(b.x - a.x, b.z - a.z) / RUN_STEP));
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    if (!open(Math.round(a.x + (b.x - a.x) * t), Math.round(a.z + (b.z - a.z) * t))) return false;
  }
  return true;
}

/**
 * The next romp from `at` (the last one `last`), drawing from `rand`: a dash to another spot of `cells` within reach
 * over open ground only (`dash` of the time, never twice running, nor when none is near), else a clip in place by the
 * clips' odds. `open` defaults to every cell (the pure tests' grids).
 */
export function nextRomp(at: Spot, cells: readonly Spot[], last: Romp["kind"] | null, rand: () => number, open: (x: number, y: number) => boolean = () => true): Romp {
  const W = WILD_ROMP;
  const near = cells.filter((c) => Math.max(Math.abs(c.x - at.x), Math.abs(c.z - at.z)) <= W.dashReach && Math.hypot(c.x - at.x, c.z - at.z) > 0.9 && clearRun(at, c, open));
  if (last !== "dash" && near.length > 0 && rand() < W.dash) {
    const to = jittered(near[Math.floor(rand() * near.length)], rand(), rand());
    if (clearRun(at, to, open)) return { kind: "dash", to };
  }
  const k = rand() * (W.zoomies + W.playbow + W.roll + W.sniff);
  if (k < W.zoomies) return { kind: "zoomies", seconds: W.zoomiesSeconds[0] + rand() * (W.zoomiesSeconds[1] - W.zoomiesSeconds[0]) };
  if (k < W.zoomies + W.playbow) return { kind: "playbow", seconds: W.playbowSeconds };
  if (k < W.zoomies + W.playbow + W.roll) return { kind: "roll", seconds: W.rollSeconds };
  return { kind: "sniff", seconds: W.sniffSeconds };
}

/**
 * Where it scampers off to when it leaves untapped: the open cell of `cells` farthest from `at` that it can reach in a
 * straight run over open ground (within `leaveReach`), where it vanishes in a puff of dust — never off the map's edge,
 * which is a cliff. Null when no such cell: it goes in a puff where it stands.
 */
export function leaveSpot(at: Spot, cells: readonly Spot[], open: (x: number, y: number) => boolean): Spot | null {
  let best: Spot | null = null, far = 0.9;
  for (const c of cells) {
    const d = Math.hypot(c.x - at.x, c.z - at.z);
    if (d > far && d <= WILD_ROMP.leaveReach && clearRun(at, c, open)) { best = c; far = d; }
  }
  return best;
}
